// ═══════════════════════════════════════════════════════════════════════════
// Bitácora local de escrituras (outbox) — Etapa 4b
//
// El operador: "mejor guardar local e intentar actualizar cuando haya
// conexión". Eso es exactamente esto, y sin que el usuario decida nada.
//
// CÓMO FUNCIONA
//   1. La app guarda en local como siempre — nada espera a la red.
//   2. Se encola una mutación con uuid propio.
//   3. Cuando hay conexión, se suben en orden.
//
// LA IDEMPOTENCIA ES LA PIEZA CLAVE. Cada mutación nace con id al CREARSE, no
// al enviarse. Reenviar por un tiempo de espera agotado o un reintento no
// duplica nada. Es justo lo que le faltaba a smartMerge —que asignaba ids
// después, con sufijo aleatorio— y lo que produjo el folio 376 fantasma.
//
// FALLOS PASAJEROS vs PERMANENTES. Distinguirlos es esencial: un error de red
// debe reintentarse, pero una mutación rechazada por RLS o por una restricción
// NUNCA va a pasar, y reintentarla para siempre BLOQUEARÍA la cola detrás de
// ella. Las permanentes se apartan y se muestran; no estorban a las demás.
// ═══════════════════════════════════════════════════════════════════════════

const CLAVE = "vt-bitacora";
const MAX_INTENTOS = 5;

// ── Almacenamiento ─────────────────────────────────────────────────────────

export function leerBitacora() {
  try {
    const b = JSON.parse(localStorage.getItem(CLAVE) || "[]");
    return Array.isArray(b) ? b : [];
  } catch { return []; }
}

function guardar(bitacora) {
  try { localStorage.setItem(CLAVE, JSON.stringify(bitacora)); }
  catch (e) {
    // Si el almacenamiento está lleno, perder la bitácora en silencio sería lo
    // peor posible: se avisa fuerte.
    console.error("[bitácora] NO SE PUDO GUARDAR — hay cambios en riesgo:", e?.message);
    throw new Error("No se pudo guardar el cambio localmente. Libera espacio y reinténtalo.",
                    { cause: e });
  }
}

export function nuevoId() {
  return (crypto?.randomUUID?.() ||
    `mut_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
}

// ── Encolar ────────────────────────────────────────────────────────────────

/**
 * Registra una mutación pendiente de subir.
 *
 * @param tabla       'client' | 'product' | …
 * @param operacion   'upsert' | 'delete'
 * @param datos       fila lista para Postgres (ya adaptada)
 * @param clave       identificador natural para deduplicar en la cola
 */
export function encolar({ tabla, operacion, datos, clave }) {
  const bitacora = leerBitacora();

  const mutacion = {
    id: nuevoId(),
    tabla, operacion, datos,
    clave: clave || datos?.legacy_id || datos?.id || null,
    creadoEn: new Date().toISOString(),
    dispositivo: dispositivo(),
    intentos: 0,
    estado: "pendiente",     // pendiente | fallida
    error: null,
  };

  // Si ya hay una mutación PENDIENTE para la misma fila, se sustituye: subir
  // cinco ediciones sucesivas del mismo cliente no aporta nada y multiplica
  // los puntos de falla. Se conserva el orden original.
  const i = bitacora.findIndex(m =>
    m.estado === "pendiente" && m.tabla === tabla &&
    m.operacion === operacion && m.clave === mutacion.clave);

  if (i >= 0) {
    mutacion.id = bitacora[i].id;              // se mantiene la identidad
    mutacion.creadoEn = bitacora[i].creadoEn;  // y el lugar en la fila
    bitacora[i] = mutacion;
  } else {
    bitacora.push(mutacion);
  }

  guardar(bitacora);
  return mutacion;
}

// ── Estado ─────────────────────────────────────────────────────────────────

export const pendientes = () => leerBitacora().filter(m => m.estado === "pendiente");
export const fallidas   = () => leerBitacora().filter(m => m.estado === "fallida");

export function resumenBitacora() {
  const b = leerBitacora();
  return {
    pendientes: b.filter(m => m.estado === "pendiente").length,
    fallidas:   b.filter(m => m.estado === "fallida").length,
    masVieja:   b.length ? b[0].creadoEn : null,
  };
}

function quitar(ids) {
  const fuera = new Set(ids);
  guardar(leerBitacora().filter(m => !fuera.has(m.id)));
}

function actualizar(id, cambios) {
  guardar(leerBitacora().map(m => (m.id === id ? { ...m, ...cambios } : m)));
}

export function reintentarFallidas() {
  guardar(leerBitacora().map(m =>
    m.estado === "fallida" ? { ...m, estado: "pendiente", intentos: 0, error: null } : m));
}

export function descartarFallidas() {
  guardar(leerBitacora().filter(m => m.estado !== "fallida"));
}

// ── Clasificación de errores ───────────────────────────────────────────────

/**
 * ¿Vale la pena reintentar este error?
 *
 * Permanente: RLS, restricción violada, columna inexistente, tipo inválido.
 * Reintentar eso para siempre bloquearía todo lo que venga detrás.
 */
export function esPasajero(error) {
  const msg = String(error?.message || error || "").toLowerCase();
  const codigo = String(error?.code || "");

  // Red o servidor caído: pasajero
  if (/failed to fetch|network|timeout|econn|fetch failed|503|502|504/.test(msg)) return true;
  if (/^(503|502|504|429)$/.test(codigo)) return true;

  // Postgres: 23xxx = violación de restricción · 42xxx = error de sintaxis o
  // de esquema · PGRST = PostgREST rechazó (incluye RLS). Todos permanentes.
  if (/^(23|42)/.test(codigo)) return false;
  if (codigo.startsWith("PGRST")) return false;
  if (/violates row-level security|permission denied|duplicate key|violates/.test(msg)) return false;

  // Desconocido: se trata como pasajero pero el contador de intentos lo acota.
  return true;
}

// ── Subida ─────────────────────────────────────────────────────────────────

/**
 * Sube las mutaciones pendientes, en orden.
 *
 * @param aplicar  async (mutación) => void ; lanza si falla
 * @returns { subidas, pendientes, fallidas }
 */
export async function subir(aplicar, { alProgreso } = {}) {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { subidas: 0, pendientes: pendientes().length, fallidas: fallidas().length, sinRed: true };
  }

  const cola = pendientes();
  const logradas = [];

  for (const m of cola) {
    try {
      await aplicar(m);
      logradas.push(m.id);
      alProgreso?.({ hechas: logradas.length, total: cola.length });
    } catch (e) {
      const intentos = (m.intentos || 0) + 1;
      if (!esPasajero(e) || intentos >= MAX_INTENTOS) {
        // Se aparta para que NO bloquee a las siguientes.
        actualizar(m.id, {
          estado: "fallida", intentos,
          error: `${e?.code ? e.code + ": " : ""}${e?.message || e}`,
        });
      } else {
        actualizar(m.id, { intentos, error: String(e?.message || e) });
        // Pasajero: se detiene aquí para no romper el ORDEN de las siguientes.
        break;
      }
    }
  }

  if (logradas.length) quitar(logradas);
  return { subidas: logradas.length, pendientes: pendientes().length, fallidas: fallidas().length };
}

function dispositivo() {
  try {
    let d = localStorage.getItem("vt-device-id");
    if (!d) {
      d = "dev_" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem("vt-device-id", d);
    }
    return d;
  } catch { return "desconocido"; }
}
